/**
 * Segment ingestion via the `@segment/analytics-next` browser SDK.
 *
 * The widget shares Dorsia's Segment source with the other web surfaces (see
 * `executive/app/lib/analytics/segment-client.ts` for the sibling client).
 * Segment is the canonical analytics pipeline — it fans out to Mixpanel, Braze,
 * the warehouse, etc. downstream — so the widget reports here and never talks to
 * a destination directly.
 *
 * The SDK manages the anonymous id (`ajs_anonymous_id` in localStorage) and the
 * `context.campaign` / referrer attribution on every call, so we no longer hand-
 * roll a distinct id or first-touch super properties.
 *
 * All events go through `lib/analytics`, never this module directly.
 */

import { AnalyticsBrowser } from '@segment/analytics-next';
import Config from '../Config.ts';
import { IS_EMBED } from '../isEmbed.ts';
import { generateUuid, getSessionId, resetSessionId } from './sessionManager.ts';

// Baked in at build time (see vite.embed.config.ts / build-embed.mjs). Guard
// every read with `typeof` so a non-embed build — where it is never defined —
// can't throw a ReferenceError.
declare const __DORSIA_WIDGET_VERSION__: string;

/**
 * Tag every event with the surface it came from. The widget shares the Segment
 * source with the member app and the marketing site, so this property is the
 * cheap filter in every downstream report (`where surface == "widget"`).
 */
const SURFACE = 'widget';

/**
 * Surface tag for the embed relay path. Distinct from `SURFACE` ('widget') so
 * downstream can tell first-party-relayed embed traffic apart from the SPA at
 * `book.dorsia.com`, which reports through the Segment browser SDK directly.
 */
const EMBED_SURFACE = 'embed';

/**
 * First-party relay endpoint (path appended to `Config.data.uri.api`, which
 * already ends in `/api`). In embed mode the Segment browser SDK is never
 * loaded — no 3rd-party script or cookies on the partner origin — so allowlisted
 * funnel events are POSTed here and forwarded to Segment server-side.
 */
const EMBED_RELAY_PATH = '/embed/analytics';

/**
 * Defensive PII denylist for the first-party relay.
 *
 * The widget funnel events (see `./events.ts`) carry only structural metadata
 * today — ids, types, booleans, a referral code — so NOTHING here is stripped in
 * practice. This list is belt-and-suspenders: if a future event ever attaches a
 * phone/email/name/token to its properties, that key is dropped in the browser
 * before the payload leaves for the relay rather than silently forwarded.
 */
const PII_PROPERTY_KEYS = [
  'phone',
  'phone_number',
  'email',
  'name',
  'first_name',
  'last_name',
  'token',
];

let cachedAnalytics: AnalyticsBrowser | null = null;
let loadAttempted = false;

function getWriteKey(): string | undefined {
  // Never load Segment inside the embed — it would run on a third-party origin
  // under the restaurant's consent regime and pollute funnel metrics with demo
  // traffic. Analytics degrades to a silent no-op.
  if (IS_EMBED) return undefined;
  const key = import.meta.env.VITE_SEGMENT_WRITE_KEY as string | undefined;
  return key && key.length > 0 ? key : undefined;
}

function dntEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  const dnt =
    navigator.doNotTrack ||
    (window as unknown as { doNotTrack?: string }).doNotTrack ||
    (navigator as unknown as { msDoNotTrack?: string }).msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
}

/**
 * A page context with the query string and hash stripped off the URL.
 *
 * Widget URLs carry referral codes and deeplink ids (`?event_id=`, referral
 * params, any future auth-like token). Segment auto-collects the full
 * `context.page.url`/`search` from `document.location`, so we override those
 * two fields with an origin+pathname-only URL — the only place those values
 * appear is the explicit properties on each event (`referral_code`, `event_id`,
 * …). We pass this in the per-call track options rather than via a source
 * middleware so the override is deterministic and independent of plugin
 * ordering: `addPageContext` (in the event factory) spreads the options-supplied
 * `context.page` last, so these fields win over the auto-collected ones.
 */
function sanitizedPageContext(): { url: string; search: string } | undefined {
  try {
    const { origin, pathname } = window.location;
    return { url: `${origin}${pathname}`, search: '' };
  } catch {
    return undefined;
  }
}

/**
 * Lazily load the Segment browser SDK. Returns `null` (a silent no-op) when
 * `VITE_SEGMENT_WRITE_KEY` is unset (e.g. local dev). The Do-Not-Track gate
 * lives in `trackSegment` so it is re-evaluated on every call.
 */
function getAnalytics(): AnalyticsBrowser | null {
  if (cachedAnalytics) return cachedAnalytics;
  if (loadAttempted) return null;
  loadAttempted = true;

  const writeKey = getWriteKey();
  if (!writeKey) return null;

  const analytics = AnalyticsBrowser.load({ writeKey });
  cachedAnalytics = analytics;
  return analytics;
}

/**
 * A per-open anonymous id for the embed relay.
 *
 * In embed mode there is no Segment browser SDK to mint/persist an
 * `anonymousId`, and we deliberately keep NO cookies or storage on the partner
 * origin. So we generate one v4 UUID per widget open, hold it in memory only,
 * and send it as the relay's `anonymousId`. It is reset alongside the Segment
 * identity (see `resetSegment`) so a shared browser / re-verification starts a
 * fresh anonymous identity — mirroring how the SDK's `reset()` works on the SPA.
 */
let embedAnonymousId: string | null = null;

function getEmbedAnonymousId(): string {
  if (!embedAnonymousId) {
    embedAnonymousId = generateUuid();
  }
  return embedAnonymousId;
}

/**
 * Drop keys on the {@link PII_PROPERTY_KEYS} denylist before relaying. Returns a
 * shallow copy so the caller's properties object is never mutated.
 */
function stripPii(properties: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!PII_PROPERTY_KEYS.includes(key)) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * Relay a funnel event to the first-party api/ endpoint, which forwards it to
 * Segment server-side using a SERVER write key. Fire-and-forget: `keepalive`
 * lets the request outlive the page (e.g. a `Widget Book Succeeded` that
 * navigates away), and both synchronous throws and async rejections are
 * swallowed so analytics can never break the booking flow.
 *
 * The browser sends NO write key and never touches `api.segment.io` — the server
 * key and Segment ingest stay entirely in api/ so the embed bundle carries no
 * secret and the partner page makes no 3rd-party request.
 */
function relayEmbedEvent(event: string, properties: Record<string, unknown>): void {
  try {
    const widgetVersion =
      typeof __DORSIA_WIDGET_VERSION__ !== 'undefined' ? __DORSIA_WIDGET_VERSION__ : 'dev';

    // Join without a double slash: `Config.data.uri.api` may or may not carry a
    // trailing slash (the embed normalizes it to a trailing slash via
    // normalizeApiBase; the SPA default has none), and EMBED_RELAY_PATH is absolute.
    const relayUrl = `${Config.data.uri.api.replace(/\/$/, '')}${EMBED_RELAY_PATH}`;
    void fetch(relayUrl, {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        event,
        anonymousId: getEmbedAnonymousId(),
        properties: {
          ...stripPii(properties),
          environment: Config.data.environment,
          surface: EMBED_SURFACE,
          session_id: getSessionId(),
          partner_origin: window.location.origin,
          widget_version: widgetVersion,
        },
      }),
    }).catch(() => {
      // analytics must never break the page
    });
  } catch {
    // analytics must never break the page
  }
}

/**
 * Track an event via the Segment browser SDK. Safe no-op when DNT is on or when
 * `VITE_SEGMENT_WRITE_KEY` is unset. Fire-and-forget and best-effort: the
 * returned promise is not awaited, and both synchronous throws and async
 * rejections are swallowed so tracking can never break the funnel.
 *
 * In embed mode the browser SDK is never loaded (no 3rd-party script/cookies on
 * the partner origin), so the event is relayed first-party instead — see
 * {@link relayEmbedEvent}.
 */
export function trackSegment(event: string, properties: Record<string, unknown>): void {
  if (typeof window === 'undefined' || dntEnabled()) return;

  if (IS_EMBED) {
    relayEmbedEvent(event, properties);
    return;
  }

  try {
    const analytics = getAnalytics();
    if (!analytics) return;

    const page = sanitizedPageContext();
    void analytics
      .track(
        event,
        {
          ...properties,
          environment: Config.data.environment,
          surface: SURFACE,
          session_id: getSessionId(),
        },
        page ? { context: { page } } : undefined,
      )
      .catch(() => {
        // analytics must never break the page
      });
  } catch {
    // analytics must never break the page
  }
}

/**
 * Associate the current anonymous session with a known member (after phone
 * verification). Segment links the persisted `anonymousId` to `userId`, so
 * downstream destinations (Mixpanel, …) merge the prior anonymous funnel events
 * into the member's profile, and every later event in the session carries the
 * `userId` automatically — no client-side buffering required.
 *
 * Same best-effort guards as `trackSegment`: no-op under DNT, a missing write
 * key, or an empty id; never throws.
 *
 * Note: `session_id` is intentionally not sent here — it is funnel-event
 * metadata attached in `trackSegment`, not a profile trait. Downstream, join
 * identify→track by `userId`/`anonymousId` (which Segment links on identify),
 * not by session.
 */
export function identifySegment(userId: string, traits?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || dntEnabled() || !userId) return;

  // No-op in embed: the relay is anonymous-only (a per-open `anonymousId`, no
  // identity stitching) so a member's traits never reach a first-party endpoint
  // from a partner page. SPA session stitching is intentionally out of scope.
  if (IS_EMBED) return;

  try {
    const analytics = getAnalytics();
    if (!analytics) return;

    const page = sanitizedPageContext();
    void analytics
      .identify(userId, traits, page ? { context: { page } } : undefined)
      .catch(() => {
        // analytics must never break the page
      });
  } catch {
    // analytics must never break the page
  }
}

/**
 * Clear the persisted Segment identity (`userId` + `anonymousId`) and start a
 * fresh anonymous session. Call this whenever the widget drops its auth, so a
 * shared browser or a re-verification flow doesn't attribute the next person's
 * pre-verification funnel events to the previously identified member.
 */
export function resetSegment(): void {
  if (typeof window === 'undefined') return;

  // Mint a fresh widget session id alongside Segment's identity reset, so the
  // next person on a shared browser doesn't inherit the prior member's session.
  resetSessionId();

  if (IS_EMBED) {
    // The embed relay has no SDK to reset; drop the per-open anonymous id so the
    // next anonymous session mints a fresh one on the next event.
    embedAnonymousId = null;
    return;
  }

  try {
    const analytics = getAnalytics();
    if (!analytics) return;

    void analytics.reset();
  } catch {
    // analytics must never break the page
  }
}
